import { MenuItem } from "@mui/material";
import { Form } from "formik";
import moment from "moment";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getArts } from "../../../../actions/arts";
import { convertDateTimeToNet } from "../../../../utils/datetimeUtils";
import {
  FormikDateRangeInput,
  FormikDateRangeSearch,
} from "../../../commons/formik/formikDateRangeSearch";
import FormikForm from "../../../commons/formik/formikForm";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledForm } from "../../../styled/form";
import { StyledPaper } from "../../../styled/paper";

yup.setLocale({
  mixed: {
    notType: "Campo inválido",
  },
});

yup.addMethod(yup.Schema, "atLeastOne", function (fields) {
  return this.test({
    name: "atLeastOne",
    message: "Debe completar al menos uno de estos campos",
    test: function (value, textContext) {
      const { parent, createError, path } = textContext;
      return fields.some((f) => {
        return parent[f] != null && parent[f] !== "";
      })
        ? true
        : createError({
          path,
          message: "Debe completar al menos uno de estos campos",
        });
    },
  });
});

const arrayAtLeastOne = ["accident", "status", "workerId", "dateFrom", "dateTo"];

const initialValues = {
  art: "",
  workerId: "",
  accident: "",
  status: "",
  datePeriod: "all",
  dateFrom: null,
  dateTo: null,
};

const SearchSiniestrosArt = ({ isSearching, handleSearch, handleReset }) => {
  const dispatch = useDispatch();
  const arts = useSelector((state) => state.arts.list);
  const loading = useSelector((state) => state.arts.isFetching);

  const roles = useSelector((state) => state.auth.roles);
  const isBackofficeRole = roles.tipo === "Soporte" || roles.tipo === "Administrador";

  const validationSchema = yup.object().shape(
    {
      art: isBackofficeRole
        ? yup.string().required("Campo obligatorio")
        : yup.string().notRequired(),
      workerId: yup
        .number("Ingrese sólo números")
        .integer("Ingrese sólo números")
        .typeError("Ingrese sólo números")
        .notRequired()
        .atLeastOne(arrayAtLeastOne),
      accident: yup.string().notRequired().atLeastOne(arrayAtLeastOne),
      status: yup.string().optional().atLeastOne(arrayAtLeastOne),
      datePeriod: yup.string().optional(),
      dateFrom: yup
        .date()
        .nullable()
        .notRequired()
        .when("dateTo", {
          is: (dateTo) => dateTo !== null && dateTo !== undefined,
          then: (schema) => schema.required("Ingrese una fecha desde la que buscar"),
        })
        .test(
          "date-from-before-to",
          "La fecha desde debe ser anterior o igual a la fecha hasta",
          function (value) {
            const { dateTo } = this.parent;
            if (!value || !dateTo) return true;
            return new Date(value) <= new Date(dateTo);
          },
        )
        .atLeastOne(arrayAtLeastOne),
      dateTo: yup
        .date()
        .nullable()
        .notRequired()
        .when("dateFrom", {
          is: (dateFrom) => dateFrom !== null && dateFrom !== undefined,
          then: (schema) => schema.required("Ingrese una fecha hasta la que buscar"),
        })
        .test(
          "date-to-after-from",
          "La fecha hasta debe ser posterior o igual a la fecha desde",
          function (value) {
            const { dateFrom } = this.parent;
            if (!value || !dateFrom) return true;
            return new Date(value) >= new Date(dateFrom);
          },
        )
        .atLeastOne(arrayAtLeastOne),
    },
    [["dateFrom", "dateTo"]],
  );

  useEffect(() => {
    if ((roles.SOPORTE || roles.ADMIN) && arts.length === 0 && !loading) {
      dispatch(getArts());
    }
  }, []);

  const handleSubmitSearch = (values) => {
    const queryObject = {};

    if (values.accident) {
      queryObject.accident = values.accident;
    }
    if (values.workerId) {
      queryObject.workerId = values.workerId;
    }
    if (values.status !== "") {
      queryObject.status = values.status === "true";
    }
    if (values.dateFrom && values.dateTo) {
      queryObject.dateFrom = convertDateTimeToNet(moment(values.dateFrom).startOf("day"));
      queryObject.dateTo = convertDateTimeToNet(moment(values.dateTo).endOf("day"));
    }
    if (values.art) {
      queryObject.idArt = values.art;
    }

    handleSearch(new URLSearchParams(queryObject).toString(), values.art);
  };

  return (
    <div>
      <StyledPaper.Container>
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitSearch}>
          {({ handleSubmit, setFieldValue }) => (
            <>
              <Form>
                {isBackofficeRole && (
                  <StyledForm.Input>
                    <FormikSelect
                      name="art"
                      label="Art"
                      variant="standard">
                      {arts.map((art) => {
                        return (
                          <MenuItem value={art.id} key={art.id}>
                            {art.nombre}
                          </MenuItem>
                        );
                      })}
                    </FormikSelect>
                  </StyledForm.Input>
                )}
                <StyledForm.Input>
                  <FormikTextField
                    name="accident"
                    label="Siniestro"
                    variant="standard"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikTextField
                    name="workerId"
                    label="Documento Trabajador"
                    variant="standard"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikSelect name="status" label="Estado" variant="standard">
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Abierto</MenuItem>
                    <MenuItem value="false">Cerrado</MenuItem>
                  </FormikSelect>
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeSearch
                    name="datePeriod"
                    label="Período"
                    variant="standard"
                    startDateFieldName="dateFrom"
                    endDateFieldName="dateTo"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput name="dateFrom" label="Fecha Desde" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput name="dateTo" label="Fecha Hasta" />
                </StyledForm.Input>
              </Form>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSearching}>
                  Buscar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="reset"
                  color="primary"
                  onClick={() => {
                    setFieldValue("art", initialValues.art);
                    setFieldValue("workerId", initialValues.workerId);
                    setFieldValue("accident", initialValues.accident);
                    setFieldValue("status", initialValues.status);
                    setFieldValue("datePeriod", initialValues.datePeriod);
                    setFieldValue("dateFrom", initialValues.dateFrom);
                    setFieldValue("dateTo", initialValues.dateTo);
                    handleReset();
                  }}>
                  Limpiar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
            </>
          )}
        </FormikForm>
      </StyledPaper.Container>
    </div>
  );
};

export default SearchSiniestrosArt;
